import { useMutation } from '@apollo/client';
import { shallowEqual, useSelector } from 'react-redux';

import {
  EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_GENERIC_ERROR,
  EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_INVOICE_ORDER_NOT_FOUND_ERROR,
  EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_ORDER_ERROR,
  EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_ORDER_EXPIRED_ERROR,
} from 'src/app/constants/errorMessages';
import { log } from 'src/app/utils/logging';
import CONFIRM_INVOICE_ORDER from 'src/graphql/confirmPendingOrder';
import type {
  ConfirmInvoiceOrderMutation,
  ConfirmInvoiceOrderMutationVariables,
} from 'src/graphql/generated';
import type AppState from 'src/types/appState';

export const useEditConfirmInvoiceOrder = () => {
  const { seekAdvertiserId, advertiserNation, interactionId, billToAgent } =
    useSelector(
      (state: AppState) => ({
        advertiserNation: state.session.hirerAccount.nation,
        billToAgent: state.session.billToAgent,
        interactionId: state.session.interactionId,
        seekAdvertiserId: state.session.seekAdvertiserId,
      }),
      shallowEqual,
    );

  const [confirmInvoiceOrderMutation] = useMutation<
    ConfirmInvoiceOrderMutation,
    ConfirmInvoiceOrderMutationVariables
  >(CONFIRM_INVOICE_ORDER, {
    context: {
      headers: {
        source: 'checkout',
        site: advertiserNation,
        'interaction-id': interactionId,
      },
    },
  });

  const confirmInvoiceOrder = async (
    idempotencyKey: string,
    orderId: string,
  ) => {
    const { data, errors } = await confirmInvoiceOrderMutation({
      variables: {
        input: {
          advertiserId: seekAdvertiserId,
          billToAgent,
          idempotencyKey,
          orderId,
        },
      },
    });

    if (errors && errors.length > 0) {
      log.error('confirmInvoiceOrder | generic', {
        errors,
        seekAdvertiserId,
        advertiserNation,
        interactionId,
        billToAgent,
      });
      throw new Error(EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_GENERIC_ERROR);
    } else if (
      data?.confirmInvoiceOrder?.__typename === 'InvoiceOrderNotFoundPayload'
    ) {
      log.error('confirmInvoiceOrder | InvoiceOrderNotFoundPayload', {
        seekAdvertiserId,
        advertiserNation,
        interactionId,
        billToAgent,
      });
      throw new Error(
        EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_INVOICE_ORDER_NOT_FOUND_ERROR,
      );
    } else if (data?.confirmInvoiceOrder?.__typename === 'OrderExpiredError') {
      log.error('confirmInvoiceOrder | OrderExpiredError', {
        seekAdvertiserId,
        advertiserNation,
        interactionId,
        billToAgent,
      });
      throw new Error(EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_ORDER_EXPIRED_ERROR);
    } else if (
      data?.confirmInvoiceOrder?.__typename === 'OrderInvalidError' ||
      data?.confirmInvoiceOrder?.__typename === 'OrderNotFoundError'
    ) {
      log.error('confirmInvoiceOrder ', {
        typename: data.confirmInvoiceOrder.__typename,
        seekAdvertiserId,
        advertiserNation,
        interactionId,
        billToAgent,
      });
      throw new Error(EDIT_PAGE_CONNECTION_CONFIRM_INVOICE_ORDER_ERROR);
    }
  };

  return confirmInvoiceOrder;
};
